import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, ExternalLink, Github, Play, FileText, Image as ImageIcon } from 'lucide-react';
import YouTubeEmbed, { getYouTubeVideoId } from '../ui/YouTubeEmbed';
import { useTranslation } from '../../hooks/useTranslation';
import { usePortfolioTranslation } from '../../hooks/usePortfolioTranslation';

export const ProjectModal = ({ project, isOpen, onClose }) => {
    const { t } = useTranslation();
    const { translateProject } = usePortfolioTranslation();
    const [currentMediaIndex, setCurrentMediaIndex] = useState(0);

    const translatedProject = translateProject(project?.translationKey);
    const displayTitle = translatedProject?.title || project?.title;
    const displayDescription = translatedProject?.description || project?.description;
    const videoRef = useRef(null);
    const [imageDimensions, setImageDimensions] = useState({ width: 'auto', height: 'auto' });

    // Reset video and dimensions when modal opens/closes or media changes
    useEffect(() => {
        if (!isOpen) {
            if (videoRef.current) {
                videoRef.current.pause();
                videoRef.current.currentTime = 0;
            }
            setCurrentMediaIndex(0);
            setImageDimensions({ width: 'auto', height: 'auto' });
        }
    }, [isOpen]);

    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.pause();
            videoRef.current.currentTime = 0;
        }
    }, [currentMediaIndex]);

    if (!isOpen || !project) return null;

    const nextMedia = () => {
        // Pause current video if playing
        if (videoRef.current) {
            videoRef.current.pause();
            videoRef.current.currentTime = 0;
        }
        setCurrentMediaIndex((prev) => (prev + 1) % project.media.items.length);
        setImageDimensions({ width: 'auto', height: 'auto' });
    };

    const prevMedia = () => {
        // Pause current video if playing
        if (videoRef.current) {
            videoRef.current.pause();
            videoRef.current.currentTime = 0;
        }
        setCurrentMediaIndex((prev) => (prev - 1 + project.media.items.length) % project.media.items.length);
        setImageDimensions({ width: 'auto', height: 'auto' });
    };

    const navigateToMedia = (index) => {
        // Pause current video if playing
        if (videoRef.current) {
            videoRef.current.pause();
            videoRef.current.currentTime = 0;
        }
        setCurrentMediaIndex(index);
        setImageDimensions({ width: 'auto', height: 'auto' });
    };

    const handleImageLoad = (event) => {
        const img = event.target;
        const containerWidth = img.parentElement.clientWidth;
        const containerHeight = 400; // Max height we want

        const aspectRatio = img.naturalWidth / img.naturalHeight;

        let width, height;

        if (img.naturalWidth > containerWidth || img.naturalHeight > containerHeight) {
            if (aspectRatio > containerWidth / containerHeight) {
                width = containerWidth;
                height = containerWidth / aspectRatio;
            } else {
                height = containerHeight;
                width = containerHeight * aspectRatio;
            }
        } else {
            width = img.naturalWidth;
            height = img.naturalHeight;
        }

        setImageDimensions({ width: `${width}px`, height: `${height}px` });
    };

    const currentMedia = project.media.items[currentMediaIndex];

    const renderMedia = () => {
        if (!currentMedia) return null;

        switch (currentMedia.type) {
            case 'video':
                return (
                    <div className="w-full min-h-[300px] bg-black rounded-lg overflow-hidden flex items-center justify-center">
                        <video
                            key={currentMedia.src}
                            ref={videoRef}
                            controls
                            controlsList="nodownload"
                            disablePictureInPicture
                            className="max-w-full object-contain"
                            style={{ maxHeight: '80vh' }}
                            poster={currentMedia.poster}
                            onContextMenu={(e) => e.preventDefault()} // Prevent right-click menu
                        >
                            <source src={currentMedia.src} type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>
                    </div>
                );
            case 'youtube':
                return (
                    <div className="w-full bg-black rounded-lg overflow-hidden">
                        <YouTubeEmbed
                            videoId={getYouTubeVideoId(currentMedia.src) || currentMedia.videoId}
                            title={currentMedia.title}
                        />
                    </div>
                );
            case 'image':
                return (
                    <div className="w-full min-h-[300px] bg-black rounded-lg overflow-hidden flex items-center justify-center">
                        <img
                            src={currentMedia.src}
                            alt={currentMedia.title}
                            className="object-contain"
                            style={{
                                width: imageDimensions.width,
                                height: imageDimensions.height,
                                maxWidth: '100%',
                                maxHeight: '500px'
                            }}
                            onLoad={handleImageLoad}
                            onContextMenu={(e) => e.preventDefault()} // Prevent right-click menu
                            onDragStart={(e) => e.preventDefault()} // Prevent drag
                            draggable={false}
                        />
                    </div>
                );
            case 'presentation':
                return (
                    <div className="w-full h-96 bg-[#1a1a1a] rounded-lg border border-[#732424]/30 flex flex-col items-center justify-center">
                        <FileText className="w-16 h-16 text-[#d92323] mb-4" />
                        <h3 className="text-white text-lg font-bold mb-2">{currentMedia.title}</h3>
                        <a
                            href={currentMedia.src}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-6 py-2 bg-[#d92323] hover:bg-[#b91b1b] text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                        >
                            <ExternalLink className="w-4 h-4" />
                            Open PDF
                        </a>
                    </div>
                );
            case 'gdrive':
                return (
                    <div className="w-full bg-black rounded-lg overflow-hidden" style={{ aspectRatio: '16/9' }}>
                        <iframe
                            src={currentMedia.src}
                            title={currentMedia.title}
                            allow="autoplay"
                            allowFullScreen
                            className="w-full h-full border-0"
                            style={{ minHeight: '360px' }}
                        />
                    </div>
                );
            default:
                return null;
        }
    };

    const getMediaIcon = (type) => {
        switch (type) {
            case 'video': return <Play className="w-4 h-4" />;
            case 'youtube': return <Play className="w-4 h-4" />;
            case 'gdrive': return <Play className="w-4 h-4" />;
            case 'image': return <ImageIcon className="w-4 h-4" />;
            case 'presentation': return <FileText className="w-4 h-4" />;
            default: return null;
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-[#141414] border border-[#732424]/30 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-[#732424]/20">
                    <div className="flex items-center gap-4">
                        {project.logo && (
                            <img
                                src={project.logo}
                                alt={`${project.title} logo`}
                                className="w-12 h-12 rounded-lg object-contain bg-white/5 p-2 border border-[#732424]/30"
                            />
                        )}
                        <div>
                            <h2 className="text-2xl font-bold text-white">{displayTitle}</h2>
                            <div className="flex items-center gap-2 mt-1">
                                {project.winner && (
                                    <span className="text-xs bg-[#d92323] text-white px-2 py-1 rounded-full font-bold">
                                        Winner
                                    </span>
                                )}
                                <div className="flex gap-1">
                                    {project.tags.slice(0, 3).map((tag, idx) => (
                                        <span key={idx} className="text-xs text-[#7b7b7b] bg-[#0d0d0d] border border-[#732424]/20 px-2 py-1 rounded">
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-[#7b7b7b] hover:text-white transition-colors p-2"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {/* Description */}
                    <p className="text-[#7b7b7b] mb-6 leading-relaxed">{displayDescription}</p>

                    {/* Media Viewer */}
                    {project.media.items.length > 0 && (
                        <div className="mb-6">
                            <div className="relative">
                                {renderMedia()}

                                {/* Navigation arrows */}
                                {project.media.items.length > 1 && (
                                    <>
                                        <button
                                            onClick={prevMedia}
                                            className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                                        >
                                            <ChevronLeft className="w-5 h-5" />
                                        </button>
                                        <button
                                            onClick={nextMedia}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                                        >
                                            <ChevronRight className="w-5 h-5" />
                                        </button>
                                    </>
                                )}

                                {/* Media counter */}
                                {project.media.items.length > 1 && (
                                    <div className="absolute bottom-4 right-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                                        {currentMediaIndex + 1} / {project.media.items.length}
                                    </div>
                                )}
                            </div>

                            {/* Media title */}
                            <div className="mt-4 text-center">
                                <h3 className="text-white font-medium flex items-center justify-center gap-2">
                                    {getMediaIcon(currentMedia.type)}
                                    {currentMedia.title}
                                </h3>
                            </div>

                            {/* Media thumbnails */}
                            {project.media.items.length > 1 && (
                                <div className="flex gap-2 mt-4 justify-center">
                                    {project.media.items.map((item, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => navigateToMedia(idx)}
                                            className={`p-2 rounded-lg border transition-colors ${idx === currentMediaIndex
                                                ? 'border-[#d92323] bg-[#d92323]/10 text-[#d92323]'
                                                : 'border-[#732424]/30 text-[#7b7b7b] hover:border-[#d92323]/50'
                                                }`}
                                        >
                                            {getMediaIcon(item.type)}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Links */}
                    {project.links && (
                        <div className="flex flex-wrap gap-3">
                            {project.links.github && (
                                <a
                                    href={project.links.github}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 px-4 py-2 bg-[#0d0d0d] border border-[#732424]/30 hover:border-[#d92323]/50 text-white rounded-lg transition-colors"
                                >
                                    <Github className="w-4 h-4" />
                                    {t('github')}
                                </a>
                            )}
                            {project.links.demo && (
                                <a
                                    href={project.links.demo}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 px-4 py-2 bg-[#d92323] hover:bg-[#b91b1b] text-white rounded-lg transition-colors"
                                >
                                    <ExternalLink className="w-4 h-4" />
                                    {t('demo')}
                                </a>
                            )}
                            {project.links.presentation && (
                                <a
                                    href={project.links.presentation}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 px-4 py-2 bg-[#0d0d0d] border border-[#732424]/30 hover:border-[#d92323]/50 text-white rounded-lg transition-colors"
                                >
                                    <FileText className="w-4 h-4" />
                                    {t('presentation')}
                                </a>
                            )}
                        </div>
                    )}

                    {/* All Tags */}
                    <div className="flex flex-wrap gap-2 mt-6 pt-6 border-t border-[#732424]/20">
                        {project.tags.map((tag, idx) => (
                            <span key={idx} className="text-xs font-mono text-[#ffffff]/80 bg-[#2a1616] px-2 py-1 rounded border border-[#732424]/30">
                                {tag}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};